import { configureApiClients } from '@api_patient/configure_api_clients';
import { UniversalConfig } from '@application/universal_config.service';
import { CellulaProvider } from '@cellula/react';
import { theme } from '@cellula/react-theme-patient';
import { ConsentProvider } from '@component/consent/mod';
import { ScreenProvider as ScreenProvider_REMOVE_THE_OTHER_ONE } from '@component/screen/screen.provider';
import { BannerNotifications, LocalPushNotifications } from '@components/notification_manager/mod';
import { PopoverManager } from '@components/popover_manager.component';
import { ScreenProvider } from '@components/screen/screen.provider';
import { DialogHandler } from '@public/src/application/dialog_handler/mod';
import { LiveAnnouncerProvider } from '@public/src/components/live_announcer/mod';
import { useCurrentLocation } from '@src/application/use_current_location';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NewContainer, useResolve } from '@visiba/instantiation';
import { MotionConfig } from 'framer-motion';
import { Fragment, PropsWithChildren } from 'react';
import { sharedResolution } from '../../src/shared_resolution';

const queryClient = new QueryClient();

// Configure API client interceptors for Angular-hosted React components.
// In the React shell this runs via shell.component.tsx; here we ensure
// the generated clients also have CSRF/header interceptors in Angular mode.
configureApiClients();

interface Props {
	useGlobals?: boolean;
	children: React.ReactNode | React.ReactNode[];
}

export function CellulaProviderWrapper(props: Props): JSX.Element | null {
	return (
		<NewContainer parentContainer={sharedResolution} useInheritanceLink>
			<GlobalProviders {...props} />
		</NewContainer>
	);
}

function GlobalProviders({ children, useGlobals = false }: Props) {
	const universalConfig = useResolve(UniversalConfig);
	const [, config] = useCurrentLocation();

	if (config == null) return null;

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	const overrides = config.theme ?? ({} as any);

	return (
		<LiveAnnouncerProvider>
			<MotionConfig reducedMotion='user'>
				<CellulaProvider
					tokenOverrides={{
						colorPrimary100: (overrides['primary_100'] as string) ?? '#F8F8F9',
						colorPrimary200: (overrides['primary_200'] as string) ?? '#EFEFF1',
						colorPrimary300: (overrides['primary_300'] as string) ?? '#E1E2E5',
						colorPrimary400: (overrides['primary_400'] as string) ?? '#D1D3D7',
						colorPrimary500: (overrides['primary_500'] as string) ?? '#B3B7BE',
						colorPrimary600: (overrides['primary_600'] as string) ?? '#9095A0',
						colorPrimary700: (overrides['primary_700'] as string) ?? '#6A6F7B',
						colorPrimary800: (overrides['primary_800'] as string) ?? '#555962',
						colorPrimary800t08: ((overrides['primary_800'] as string) ?? '#555962') + '14', // Adding opacity 8% from primary 800
						colorPrimary800t12: ((overrides['primary_800'] as string) ?? '#555962') + '1F', // Adding opacity 12% from primary 800
						colorPrimary900: (overrides['primary_900'] as string) ?? '#464951',
						colorPrimary1000: (overrides['primary_1000'] as string) ?? '#2F3136',
						colorAccent100: (overrides['accent_100'] as string) ?? '#F8F8F9',
						colorAccent200: (overrides['accent_200'] as string) ?? '#EFEFF1',
						colorAccent300: (overrides['accent_300'] as string) ?? '#E1E2E5',
						colorAccent400: (overrides['accent_400'] as string) ?? '#D1D3D7',
						colorAccent500: (overrides['accent_500'] as string) ?? '#B3B7BE',
						colorAccent600: (overrides['accent_600'] as string) ?? '#9095A0',
						colorAccent700: (overrides['accent_700'] as string) ?? '#6A6F7B',
						colorAccent800: (overrides['accent_800'] as string) ?? '#555962',
						colorAccent800t08: ((overrides['accent_800'] as string) ?? '#555962') + '14', // Adding opacity 8% from accent 800
						colorAccent800t12: ((overrides['accent_800'] as string) ?? '#555962') + '1F', // Adding opacity 12% from accent 800
						colorAccent900: (overrides['accent_900'] as string) ?? '#464951',
						colorAccent1000: (overrides['accent_1000'] as string) ?? '#2F3136',
					}}
					lang={universalConfig.selectedLanguageCode.value}
					theme={theme}
					providedInRoot
				>
					<ScreenProvider target='innerRoot'>
						<ConsentProvider>
							<QueryClientProvider client={queryClient}>
								<DialogHandler />

								{useGlobals ? (
									<Fragment>
										<PopoverManager />
										<LocalPushNotifications />
										<BannerNotifications />
									</Fragment>
								) : null}

								<REACT_ONLY_PATCH_WTF_IS_GOING_ON_WITH_THIS_CODE_BASE>{children}</REACT_ONLY_PATCH_WTF_IS_GOING_ON_WITH_THIS_CODE_BASE>
							</QueryClientProvider>
						</ConsentProvider>
					</ScreenProvider>
				</CellulaProvider>
			</MotionConfig>
		</LiveAnnouncerProvider>
	);
}

function REACT_ONLY_PATCH_WTF_IS_GOING_ON_WITH_THIS_CODE_BASE(props: PropsWithChildren) {
	return <ScreenProvider_REMOVE_THE_OTHER_ONE target='innerRoot'>{props.children}</ScreenProvider_REMOVE_THE_OTHER_ONE>;
}
